import React from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';
import type { CongViecDuAnCheck } from '@/types/action-plan/DuAnView';
import CustomTooltip from '@/components/common/Tooltip';

export type ViTriCheckItem = CongViecDuAnCheck & { tenCongViec?: string };

function SortRow({ item, isCurrent }: { item: ViTriCheckItem; isCurrent?: boolean }) {
  const id = String(item.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    marginBottom: 4,
    borderRadius: 6,
    background: isCurrent ? '#eff6ff' : '#f9fafb',
    border: isCurrent ? '1px solid #93c5fd' : '1px solid #e5e7eb',
    fontSize: 13,
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <CustomTooltip value={"Kéo để đổi vị trí"} placement="left">
        <HolderOutlined style={{ color: '#9ca3af', flexShrink: 0 }} />
        </CustomTooltip>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.tenCongViec || id}
      </span>
      {/* <span style={{ color: '#6b7280', flexShrink: 0 }}>{item.viTri ?? 0}</span> */}
    </div>
  );
}

interface ViTriSiblingDragListProps {
  items: ViTriCheckItem[];
  currentId?: string;
  onChange: (next: ViTriCheckItem[]) => void;
}

export const ViTriSiblingDragList: React.FC<ViTriSiblingDragListProps> = ({
  items,
  currentId,
  onChange,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );
  const ids = items.map((i) => String(i.id));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(items, oldIndex, newIndex).map((it, idx) => ({
      ...it,
      viTri: idx,
    }));
    onChange(moved);
  };

  if (items.length === 0) {
    return <div style={{ fontSize: 13, color: '#9ca3af' }}>Không có công việc cùng cấp</div>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {items.map((it) => (
            <SortRow key={String(it.id)} item={it} isCurrent={String(it.id) === String(currentId)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
